import React from 'react';
import { motion } from 'framer-motion';
import {
    Sparkles, Copy, Calendar, Download,
    CheckCircle, Loader, Zap
} from 'lucide-react';

/**
 * QuickActionsBar - Sticky bottom bar with batch actions
 * 
 * Design matches the Pixar studio aesthetic:
 * - Glassmorphism effect with backdrop blur
 * - Gradient buttons for primary actions
 * - Consistent rounded corners and shadows
 */
const QuickActionsBar = ({
    selectedCount = 0,
    totalCount = 0,
    onGenerateAll,
    onCopyAll,
    onScheduleAll,
    onExportAll,
    isProcessing = false
}) => {
    const hasSelection = selectedCount > 0;

    return (
        <div className="sticky bottom-0 left-0 right-0 z-20">
            {/* Gradient fade overlay */}
            <div className="absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none"></div>

            {/* Actions Bar Container */}
            <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="mx-6 mb-4"
            >
                <div className="bg-white/80 backdrop-blur-xl rounded-[24px] border-2 border-slate-200/60 shadow-xl shadow-slate-200/50 p-4">
                    <div className="flex items-center justify-between">
                        {/* Left: Selection Info */}
                        <div className="flex items-center gap-4">
                            {/* Selection Counter */}
                            <div className="flex items-center gap-2">
                                <div className={`
                                    w-8 h-8 rounded-xl flex items-center justify-center font-bold text-sm
                                    ${hasSelection
                                        ? 'bg-indigo-100 text-indigo-600'
                                        : 'bg-slate-100 text-slate-400'}
                                `}>
                                    {hasSelection ? selectedCount : '0'}
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-slate-900">
                                        {hasSelection
                                            ? `${selectedCount} item${selectedCount > 1 ? 's' : ''} selected`
                                            : 'No selection'}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                        {totalCount} items available
                                    </p>
                                </div>
                            </div>

                            {/* Quick Select Buttons */}
                            <div className="hidden sm:flex items-center gap-2 pl-4 border-l border-slate-200">
                                <button className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
                                    All
                                </button>
                                <button className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
                                    None
                                </button>
                                <button className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
                                    Generated
                                </button>
                            </div>
                        </div>

                        {/* Right: Action Buttons */}
                        <div className="flex items-center gap-3">
                            {/* Secondary Actions */}
                            <div className="hidden md:flex items-center gap-2">
                                <button
                                    onClick={onCopyAll}
                                    disabled={!hasSelection || isProcessing}
                                    className={`
                                        flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all
                                        ${hasSelection && !isProcessing
                                            ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            : 'bg-slate-50 text-slate-300 cursor-not-allowed'}
                                    `}
                                >
                                    <Copy size={14} />
                                    <span>Copy All</span>
                                </button>

                                <button
                                    onClick={onExportAll}
                                    disabled={!hasSelection || isProcessing}
                                    className={`
                                        flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all
                                        ${hasSelection && !isProcessing
                                            ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            : 'bg-slate-50 text-slate-300 cursor-not-allowed'}
                                    `}
                                >
                                    <Download size={14} />
                                    <span>Export</span>
                                </button>
                            </div>

                            {/* Divider */}
                            <div className="hidden md:block w-px h-8 bg-slate-200"></div>

                            {/* Primary Actions */}
                            <button
                                onClick={onScheduleAll}
                                disabled={!hasSelection || isProcessing}
                                className={`
                                    flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all
                                    ${hasSelection && !isProcessing
                                        ? 'bg-white border-2 border-slate-200 text-slate-700 hover:border-indigo-200 hover:text-indigo-600'
                                        : 'bg-slate-50 text-slate-300 cursor-not-allowed border-2 border-transparent'}
                                `}
                            >
                                <Calendar size={14} />
                                <span className="hidden sm:inline">Schedule</span>
                            </button>

                            <button
                                onClick={onGenerateAll}
                                disabled={isProcessing}
                                className={`
                                    flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all shadow-lg
                                    ${!isProcessing
                                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-xl hover:scale-[1.02] shadow-indigo-200'
                                        : 'bg-slate-300 text-slate-500 cursor-not-allowed'}
                                `}
                            >
                                {isProcessing ? (
                                    <>
                                        <Loader size={14} className="animate-spin" />
                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={14} />
                                        <span>Generate All</span>
                                        <Zap size={12} className="text-yellow-300" />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default QuickActionsBar;